import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { Subject, takeUntil } from 'rxjs';

// The variable_schema meta-schema declares draft 2020-12, so schemas may carry
// "$schema": ".../2020-12" and must be compiled with the 2020-12 build of ajv
// (the default Ajv export only understands draft-07 and throws "no schema with
// key or ref .../2020-12"). Mirrors the edit-variable-schema dialog (SCRUM-751).
import Ajv2020 from 'ajv/dist/2020';
import addFormats from 'ajv-formats';

/** Parsed field descriptor used internally for rendering. */
interface SchemaField {
  name: string;
  type: string;
  format?: string;
  widget?: string;
  label: string;
  helpText?: string;
  required: boolean;
  defaultValue?: unknown;
  enumValues?: string[];
  enumLabels?: string[];
  minimum?: number;
  maximum?: number;
  order: number;
}

@Component({
  selector: 'app-variable-schema-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatRadioModule,
    MatDatepickerModule,
    MatIconModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './variable-schema-form.component.html',
  styleUrls: ['./variable-schema-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VariableSchemaFormComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() schema: any;
  @Input() initialValues?: Record<string, unknown>;
  @Output() valuesChange = new EventEmitter<Record<string, unknown>>();
  @Output() validChange = new EventEmitter<boolean>();

  form = new FormGroup({});
  fields: SchemaField[] = [];
  title?: string;
  description?: string;
  fieldErrors: Record<string, string> = {};

  private destroy$ = new Subject<void>();
  private ajv = new Ajv2020({ allErrors: true, coerceTypes: false, strict: false });

  constructor() {
    addFormats(this.ajv);
  }

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schema'] && !changes['schema'].firstChange) {
      this.buildForm();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildForm(): void {
    if (!this.schema?.properties) return;

    this.title = this.schema['x-title'];
    this.description = this.schema['x-description'];

    const required: string[] = this.schema.required || [];
    const properties = this.schema.properties as Record<string, any>;

    // Parse fields
    this.fields = Object.entries(properties)
      .map(([name, prop]) => ({
        name,
        type: prop.type || 'string',
        format: prop.format,
        widget: prop['x-widget'],
        label: prop['x-label'] || name,
        helpText: prop['x-helpText'],
        required: required.includes(name),
        defaultValue: prop.default,
        enumValues: prop.enum,
        enumLabels: prop['x-enumLabels'],
        minimum: prop.minimum,
        maximum: prop.maximum,
        order: prop['x-order'] ?? 9999,
      }))
      .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name));

    // Build FormGroup
    const controls: Record<string, FormControl> = {};
    for (const field of this.fields) {
      const initial =
        this.initialValues?.[field.name] ?? field.defaultValue ?? '';
      const validators = [];

      if (field.required) {
        validators.push(Validators.required);
      }
      if (field.type === 'integer' || field.type === 'number') {
        if (field.minimum != null)
          validators.push(Validators.min(field.minimum));
        if (field.maximum != null)
          validators.push(Validators.max(field.maximum));
      }

      controls[field.name] = new FormControl(initial, validators);
    }

    this.form = new FormGroup(controls);

    // Listen for changes
    this.form.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.emitValues();
    });

    // Emit initial state
    this.emitValues();
  }

  private emitValues(): void {
    const raw = this.form.getRawValue() as Record<string, any>;
    const values: Record<string, unknown> = {};

    // Coerce types to match JSON Schema expectations
    for (const field of this.fields) {
      const val = raw[field.name];
      if (val === '' || val === null || val === undefined) {
        // Skip empty optional fields
        if (!field.required) continue;
      }

      // Date fields: convert Date object to YYYY-MM-DD string
      if (field.format === 'date' && val instanceof Date) {
        const y = val.getFullYear();
        const m = String(val.getMonth() + 1).padStart(2, '0');
        const d = String(val.getDate()).padStart(2, '0');
        values[field.name] = `${y}-${m}-${d}`;
        continue;
      }

      if (
        (field.type === 'integer' || field.type === 'number') &&
        typeof val === 'string'
      ) {
        const num = Number(val);
        if (!isNaN(num)) {
          values[field.name] = field.type === 'integer' ? Math.round(num) : num;
          continue;
        }
      }

      if (field.type === 'boolean' && typeof val !== 'boolean') {
        values[field.name] = !!val;
        continue;
      }

      values[field.name] = val;
    }

    // Validate with ajv
    const validate = this.ajv.compile(this.schema);
    const valid = validate(values);
    this.fieldErrors = {};

    if (!valid && validate.errors) {
      for (const err of validate.errors) {
        // Map instancePath like "/count" → "count"
        const fieldName = err.instancePath?.replace(/^\//, '') || '';
        if (fieldName) {
          this.fieldErrors[fieldName] = err.message || 'Invalid value';
        }
      }
    }

    const formValid = valid === true && this.form.valid;
    this.valuesChange.emit(values);
    this.validChange.emit(formValid);
  }

  /** Get the display label for an enum option. */
  getEnumLabel(field: SchemaField, index: number): string {
    return field.enumLabels?.[index] || field.enumValues?.[index] || '';
  }

  /** Whether to render as radio group (≤4 options) vs select dropdown. */
  useRadioGroup(field: SchemaField): boolean {
    return (field.enumValues?.length ?? 0) <= 4;
  }

  /** Get field-level error message for display. */
  getError(field: SchemaField): string | null {
    const control = this.form.get(field.name);
    if (control?.hasError('required')) {
      return `${field.label} is required`;
    }
    if (control?.hasError('min')) {
      return `Must be at least ${field.minimum}`;
    }
    if (control?.hasError('max')) {
      return `Must be at most ${field.maximum}`;
    }
    if (this.fieldErrors[field.name]) {
      return this.fieldErrors[field.name];
    }
    return null;
  }

  /** Whether the field control has been touched and has errors. */
  hasError(field: SchemaField): boolean {
    const control = this.form.get(field.name);
    return (
      (control?.invalid && control?.touched) || !!this.fieldErrors[field.name]
    );
  }
}
